// Host-based brand profiles. Mirrors app/api.py:_BRAND_PROFILES + _HOST_TO_BRAND.
// Used to swap favicon, header logo, and visible product name based on the
// inbound hostname so partner deployments (e.g. measurement.blotout.io)
// see their own branding while running the same React code as app.9fs.ai.

export interface BrandProfile {
  key: string
  name: string
  favicon: string
  logo: string
  // Logo aspect: '1:1' fits the square 32×32 spot. Wordmark logos like
  // Blotout's are wide (~280×65) — set 'wide' so AppShell renders height-fixed,
  // width:auto and doesn't squash them into a 32×32 square.
  logoAspect: '1:1' | 'wide'
}

const PROFILES: Record<string, BrandProfile> = {
  '9fig': {
    key: '9fig',
    name: '9Fig',
    favicon: '/favicon.svg',
    logo: '/favicon.svg',
    logoAspect: '1:1',
  },
  blotout: {
    key: 'blotout',
    name: 'Blotout',
    favicon: '/brands/blotout/logo.svg',
    logo: '/brands/blotout/logo.svg',
    logoAspect: 'wide',
  },
}

const HOST_TO_BRAND: Record<string, string> = {
  'app.9fs.ai': '9fig',
  'staging.9fs.ai': '9fig',
  'localhost': '9fig',
  '127.0.0.1': '9fig',
  'measurement.blotout.io': 'blotout',
}

// Resolve the current brand at runtime. Reads window.location.host so the
// same bundle serves every partner — no build-time per-brand artifacts.
export function getBrand(): BrandProfile {
  if (typeof window === 'undefined') return PROFILES['9fig']
  const host = window.location.host.toLowerCase().split(':')[0]
  const key = HOST_TO_BRAND[host] || '9fig'
  return PROFILES[key]
}

// Apply brand to document <title> + <link rel="icon">. Call once from
// AppShell on mount. Safe to call multiple times.
export function applyBrandToDocument(brand: BrandProfile): void {
  if (typeof document === 'undefined') return
  if (document.title !== brand.name) document.title = brand.name
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }
  if (link.href !== window.location.origin + brand.favicon) {
    link.href = brand.favicon
  }
}
